import { type FC, useState } from 'react';

import { detailListClass } from '@eneco/custom-components/flows/DetailListItem.css';
import { Layout } from '@eneco/flows/components';
import { Box, Grid, Stack } from '@eneco/sparky';

import {
  SummaryAgreement,
  SummaryDetailsList,
  SummaryPaymentCard,
  SummaryStepDialogs,
  SummaryStepHeader,
  useExpN522SummaryStep,
} from './SummaryStepShared';
import { SummaryContractNotifications, SummaryStepError, SummaryProductsOverview } from '../../../../shared/components';
import { PriceDetailsOverview } from '../../../components/PriceDetailsOverview/PriceDetailsOverview';

/** Experiment summary step variant B. Uses shared summary logic with products overview in the left column. */
export const SummaryStep: FC = () => {
  const {
    label,
    flowContext,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
    selectedOffer,
    selectedOfferTermsAndConditions,
    form,
    selectedCashbackTexts,
    textualDataKey,
    textualData,
    isNavigating,
    isTermsDialogOpen,
    setIsTermsDialogOpen,
    isContractPeriodDialogOpen,
    setIsContractPeriodDialogOpen,
    isConditionsDialogOpen,
    setIsConditionsDialogOpen,
    isTariffsDialogOpen,
    setIsTariffsDialogOpen,
    displayName,
    formattedAddress,
    contractPeriod,
    goToStep,
  } = useExpN522SummaryStep();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitSuccessful },
  } = form;

  const [costPeriod, setCostPeriod] = useState('costsMonthly');
  void costPeriod;
  const { isPersonalDetailsEditProhibited } = flowContext;

  return (
    <Layout.Content variant="B" isLoading={isLoading || isNavigating} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <SummaryStepHeader />
          <SummaryContractNotifications selectedOffer={selectedOffer} notifications={textualData?.notifications} />

          <Box className={detailListClass}>
            <Stack direction="column" gap="3">
              <Stack.Item>
                <SummaryDetailsList
                  displayName={displayName}
                  birthdate={flowContext.birthdate}
                  birthDateLabel={textualData?.personalDetailsOverview?.birthDateLabel?.value}
                  street={formattedAddress.street}
                  postalCodeCity={formattedAddress.postalCodeCity}
                  contractPeriod={contractPeriod}
                  emailAddress={flowContext.emailAddress ?? ''}
                  phoneNumber={flowContext.phoneNumber ?? ''}
                  isMoving={flowContext.isMoving}
                  onGoToStep={goToStep}
                  onOpenContractPeriod={() => setIsContractPeriodDialogOpen(true)}
                  onOpenTerms={() => setIsTermsDialogOpen(true)}
                />
              </Stack.Item>

              <Grid gridTemplateColumns="minmax(0, 1fr)" gap="3">
                <Grid.Item order={{ initial: 2, md: 1 }}>
                  {selectedOffer && (
                    <SummaryProductsOverview
                      textualData={textualData}
                      offer={selectedOffer}
                      flowContext={flowContext}
                      textualDataKey={textualDataKey}
                      setCostPeriod={setCostPeriod}
                      selectedCashbackTexts={selectedCashbackTexts}
                      cashbackValue={Number(selectedOffer?.costDiscounts?.cashback?.value ?? 0)}
                      cashbackType={selectedOffer?.costDiscounts?.cashback?.type}
                      priceDetails={
                        <PriceDetailsOverview
                          flowContext={flowContext}
                          textualData={textualData}
                          offer={selectedOffer}
                        />
                      }
                    />
                  )}
                </Grid.Item>

                <Grid.Item order={{ initial: 1, md: 2 }}>
                  <SummaryPaymentCard
                    textualData={textualData?.paymentDetails}
                    register={register}
                    ibanError={errors.iban}
                    isPersonalDetailsEditProhibited={isPersonalDetailsEditProhibited}
                  />
                </Grid.Item>
              </Grid>
            </Stack>
          </Box>

          <SummaryAgreement
            nextStepText={textualData?.content?.nextStepText?.value}
            isLoading={isSubmitSuccessful}
            onOpenConditions={() => setIsConditionsDialogOpen(true)}
            onOpenTariffs={() => setIsTariffsDialogOpen(true)}
            textualData={textualData}
          />

          <SummaryStepDialogs
            label={label}
            flowContext={flowContext}
            textualData={textualData}
            selectedOffer={selectedOffer}
            selectedOfferTermsAndConditions={selectedOfferTermsAndConditions}
            isTermsDialogOpen={isTermsDialogOpen}
            setIsTermsDialogOpen={setIsTermsDialogOpen}
            isContractPeriodDialogOpen={isContractPeriodDialogOpen}
            setIsContractPeriodDialogOpen={setIsContractPeriodDialogOpen}
            isConditionsDialogOpen={isConditionsDialogOpen}
            setIsConditionsDialogOpen={setIsConditionsDialogOpen}
            isTariffsDialogOpen={isTariffsDialogOpen}
            setIsTariffsDialogOpen={setIsTariffsDialogOpen}
          />
        </>
      )}

      {(isFailed || isOrderFailed) && (
        <SummaryStepError
          isNotAuthenticated={isNotAuthenticated}
          isNotAvailable={isNotAvailable}
          isNotFound={isNotFound}
          isOrderFailed={isOrderFailed}
          textualData={textualData?.genericError}
        />
      )}
    </Layout.Content>
  );
};
